import { useState, useCallback } from 'react';
import {
  downloadFile,
  downloadFileFromApi,
  DownloadOptions,
  ApiDownloadOptions,
  DownloadResult,
} from '@/utils/downloadUtils';
import { toast } from '@/utils/toast';

export interface UseDownloadFileOptions {
  showToast?: boolean;
  successMessage?: string;
  errorMessage?: string;
}

export interface UseDownloadFileReturn {
  download: (fileUrl: string, filename?: string) => Promise<DownloadResult>;
  downloadFromApi: (
    apiEndpoint: string,
    filename?: string
  ) => Promise<DownloadResult>;
  isLoading: boolean;
  progress: number;
  error: Error | null;
  reset: () => void;
}

/**
 * Hook for downloading files with loading state and progress
 */
export function useDownloadFile(
  options: UseDownloadFileOptions = {}
): UseDownloadFileReturn {
  const {
    showToast = true,
    successMessage = 'File downloaded successfully',
    errorMessage = 'Failed to download file',
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const reset = useCallback(() => {
    setIsLoading(false);
    setProgress(0);
    setError(null);
  }, []);

  const download = useCallback(
    async (fileUrl: string, filename?: string): Promise<DownloadResult> => {
      reset();
      setIsLoading(true);
      setProgress(0);

      const downloadOptions: DownloadOptions = {
        filename,
        showLoading: true,
        onProgress: progressValue => {
          setProgress(progressValue);
        },
        onSuccess: () => {
          if (showToast) {
            toast.success(successMessage);
          }
        },
        onError: downloadError => {
          setError(downloadError);
          if (showToast) {
            toast.error(errorMessage);
          }
        },
      };

      try {
        const result = await downloadFile(fileUrl, downloadOptions);

        if (result.success) {
          setProgress(100);
        }

        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [showToast, successMessage, errorMessage, reset]
  );

  const downloadFromApi = useCallback(
    async (apiEndpoint: string, filename?: string): Promise<DownloadResult> => {
      reset();
      setIsLoading(true);
      setProgress(0);

      const downloadOptions: ApiDownloadOptions = {
        apiEndpoint,
        filename,
        showLoading: true,
        onProgress: progressValue => {
          setProgress(progressValue);
        },
        onSuccess: () => {
          if (showToast) {
            toast.success(successMessage);
          }
        },
        onError: downloadError => {
          setError(downloadError);
          if (showToast) {
            toast.error(errorMessage);
          }
        },
      };

      try {
        const result = await downloadFileFromApi(downloadOptions);

        if (result.success) {
          setProgress(100);
        }

        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [showToast, successMessage, errorMessage, reset]
  );

  return {
    download,
    downloadFromApi,
    isLoading,
    progress,
    error,
    reset,
  };
}
